/**
 * Versioned HTTP contract — `v1` — for the durable cross-service event hub
 * (`services/hub-api`), backing the `DurableHub` port (ADR 0067). Same rationale as the
 * other `./http/*` contracts (ADR 0001/0005/0023 §5): publishers, consumers, and the hub
 * gateway must parse the SAME shapes without importing one another.
 *
 * Two surfaces:
 * - **publish** (`POST /v1/hub/publish`) — a publisher appends one integration event to the
 *   outbox. Idempotent on `eventId` (a re-publish of the same id is a no-op — ADR 0067 §2/§6).
 * - **poll / ack / nack / overview** — server-internal (a consuming service's worker ↔ hub),
 *   gated like `/v1/runs/lease` (Cloudflare Access, no principal — the worker runs a consumer
 *   group across owners; the *event's* `ownerId` scopes each effect).
 *
 * Breaking changes ship as `v2`, never by mutation. The per-event payload schemas below are
 * the boundary parse for each integration event; a frozen golden fixture is parsed through
 * each by the compatibility gate (`tests/unit/integration-hub.test.ts`), so tightening or
 * removing a field fails CI while adding an OPTIONAL field — or a whole new event — passes.
 */
import { z } from 'zod';
import { apiEnvelope } from './v1.js';
// The per-event payload schemas + the name enum are DERIVED from the single central registry
// (`../integration/registry.ts`); this contract just wires them into the HTTP envelope. They are
// exported from the package via the registry (the barrel), so importers are unaffected.
import { INTEGRATION_EVENT_PAYLOAD_SCHEMAS, IntegrationEventNameSchema } from '../integration/registry.js';

const id = z.string().min(1).max(64);
const consumer = z.string().min(1).max(64);

// ── The on-the-wire event (envelope + name + event-specific payload) ─────────────────

/**
 * One integration event as it crosses the wire. The generic `payload` is parsed against the
 * named event's schema in {@link INTEGRATION_EVENT_PAYLOAD_SCHEMAS} via a refinement, so a
 * mismatched body is rejected at the boundary (parse, don't validate — ADR 0005).
 */
export const IntegrationEventWireSchema = z
  .object({
    /** ULID minted by the publisher; the effect-idempotency key. */
    eventId: id,
    name: IntegrationEventNameSchema,
    sourceService: z.string().min(1).max(64),
    /** Security principal — consumer effects are scoped to this owner. */
    ownerId: id,
    correlationId: id,
    causationId: id.optional(),
    orderingKey: z.string().min(1).max(200).optional(),
    occurredAt: z.number().int().nonnegative(),
    payload: z.record(z.unknown()),
  })
  .superRefine((ev, ctx) => {
    const schema = INTEGRATION_EVENT_PAYLOAD_SCHEMAS[ev.name];
    if (!schema.safeParse(ev.payload).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payload'],
        message: `payload does not match the ${ev.name} schema`,
      });
    }
  });
export type IntegrationEventWire = z.infer<typeof IntegrationEventWireSchema>;

// ── POST /v1/hub/publish ─────────────────────────────────────────────────────────────

/** Request: append one integration event to the outbox. Idempotent on `eventId`. */
export const PublishEventRequestSchema = IntegrationEventWireSchema;
export type PublishEventRequest = z.infer<typeof PublishEventRequestSchema>;
/** Response: the stored id and whether it was a duplicate (idempotent re-publish). */
export const PublishEventResponseSchema = apiEnvelope(
  z.object({ eventId: id, deduped: z.boolean() }),
);
export type PublishEventResponse = z.infer<typeof PublishEventResponseSchema>;

// ── POST /v1/hub/subscribe (register a consumer group + create its queue) ─────────────

/**
 * Request: register a consumer group's interest. Creates the group's pgmq queue (idempotent) and
 * records the subscription so `publish` fans out to it. `ordered` marks the group as strictly
 * ordered — it MUST then be drained by a single consumer (ADR 0067 Amendment, invariant O1).
 */
export const SubscribeRequestSchema = z.object({
  consumer,
  names: z.array(IntegrationEventNameSchema).min(1).max(64),
  ordered: z.boolean().optional(),
});
export type SubscribeRequest = z.infer<typeof SubscribeRequestSchema>;
export const SubscribeResponseSchema = apiEnvelope(z.object({ subscribed: z.literal(true) }));
export type SubscribeResponse = z.infer<typeof SubscribeResponseSchema>;

// ── POST /v1/hub/poll (server-internal: a consumer group reads its queue) ─────────────

/** Request: read the next messages from `consumer`'s queue (the group must be subscribed first). */
export const PollEventsRequestSchema = z.object({
  consumer,
  /** Max messages to read this poll (bounded; defaults server-side). */
  limit: z.number().int().positive().max(256).optional(),
  /** Visibility timeout for the read in ms (defaults server-side). */
  leaseMs: z.number().int().positive().optional(),
});
export type PollEventsRequest = z.infer<typeof PollEventsRequestSchema>;

/** One read message: the event plus pgmq delivery bookkeeping. */
export const LeasedEventSchema = z.object({
  event: IntegrationEventWireSchema,
  /** pgmq message id — the RECEIPT used to ack (delete) or dead-letter (archive) this message. */
  msgId: z.number().int().nonnegative(),
  /** pgmq `read_ct` — how many times this message has been read (1 on first read; at-least-once). */
  attempts: z.number().int().positive(),
  /** Epoch ms the visibility timeout expires; a lapsed VT makes the message visible again. */
  leaseExpiresAt: z.number().int().nonnegative(),
});
export type LeasedEvent = z.infer<typeof LeasedEventSchema>;
/** Response: the leased deliveries (empty when the consumer is caught up). */
export const PollEventsResponseSchema = apiEnvelope(z.array(LeasedEventSchema));
export type PollEventsResponse = z.infer<typeof PollEventsResponseSchema>;

// ── POST /v1/hub/ack & /v1/hub/nack (server-internal) ────────────────────────────────

/** A classified consumer failure carried on a nack (mirrors the runs `RunError`). */
export const HubErrorSchema = z.object({
  kind: z.string().min(1).max(64),
  message: z.string().max(2000),
});
export type HubError = z.infer<typeof HubErrorSchema>;

/** Request: ack a message (pgmq delete) by its receipt. */
export const AckEventRequestSchema = z.object({ consumer, msgId: z.number().int().nonnegative() });
export type AckEventRequest = z.infer<typeof AckEventRequestSchema>;

/**
 * Request: record a failed attempt. Under `maxAttempts` the message is simply left un-acked and
 * reappears after its visibility timeout (at-least-once); at/above `maxAttempts` the hub archives it
 * (the DLQ). `attempts` is the message's `read_ct` the consumer just saw. For an ORDERED queue the
 * drain loop HALTS on a poison message rather than archive-and-skip (ADR 0067 Amendment, O1).
 */
export const NackEventRequestSchema = z.object({
  consumer,
  msgId: z.number().int().nonnegative(),
  attempts: z.number().int().positive(),
  maxAttempts: z.number().int().nonnegative(),
  error: HubErrorSchema,
});
export type NackEventRequest = z.infer<typeof NackEventRequestSchema>;

/** Response for ack/nack: acknowledgement. */
export const AckEventResponseSchema = apiEnvelope(z.object({ acked: z.literal(true) }));
export type AckEventResponse = z.infer<typeof AckEventResponseSchema>;

// ── POST /v1/hub/lock & /v1/hub/unlock (single-consumer leader lease — invariant O1) ──

const holder = z.string().min(1).max(80);

/** Request: acquire/renew the leader lease for a consumer group's queue. */
export const AcquireLeaseRequestSchema = z.object({
  consumer,
  /** Unique id of the acquiring drainer instance (e.g. the DO id). */
  holder,
  /** Lease TTL in ms — the holder must renew before it expires. */
  ttlMs: z.number().int().positive().max(600_000),
});
export type AcquireLeaseRequest = z.infer<typeof AcquireLeaseRequestSchema>;
/** Response: whether the lease is now held by this holder. */
export const AcquireLeaseResponseSchema = apiEnvelope(z.object({ acquired: z.boolean() }));
export type AcquireLeaseResponse = z.infer<typeof AcquireLeaseResponseSchema>;

/** Request: release the lease (only succeeds if this holder owns it). */
export const ReleaseLeaseRequestSchema = z.object({ consumer, holder });
export type ReleaseLeaseRequest = z.infer<typeof ReleaseLeaseRequestSchema>;

// ── POST /v1/hub/overview (diagnostics) ──────────────────────────────────────────────

/**
 * The hub state in one read — the cross-service pipeline's diagnostics surface (mirrors
 * `RunsOverview`). `stalledLeases` counts leases past their visibility timeout (a crashed
 * consumer; reclaim-eligible); `haltedKeys` counts ordering keys paused by a poison event;
 * `deadDeliveries` is the DLQ depth.
 */
export const HubOverviewSchema = z.object({
  outboxEvents: z.number().int().nonnegative(),
  queuedDeliveries: z.number().int().nonnegative(),
  deadDeliveries: z.number().int().nonnegative(),
  stalledLeases: z.number().int().nonnegative(),
  haltedKeys: z.number().int().nonnegative(),
  oldestUnackedAgeMs: z.number().int().nonnegative().nullable(),
});
export type HubOverview = z.infer<typeof HubOverviewSchema>;
/** Request: read the hub overview (empty body; POST for `/v1` uniformity). */
export const HubOverviewRequestSchema = z.object({});
export type HubOverviewRequest = z.infer<typeof HubOverviewRequestSchema>;
/** Response: the overview. */
export const HubOverviewResponseSchema = apiEnvelope(HubOverviewSchema);
export type HubOverviewResponse = z.infer<typeof HubOverviewResponseSchema>;

// ── Event Inspector read API (DEVOP-40, ADR 0067 amendment) ────────────────────────────
// Read-only, Access-gated views over the `hub.events` ledger + the `hub.deliveries` audit trail.
// Names are parsed tolerantly (`z.string()`, not the closed enum) so a ledger row for a since-removed
// event still reads; the app derives topic/title/validity from the registry and degrades gracefully.

/** A consumer group's delivery status for one event (the audit-trail state machine). */
export const DeliveryStatusSchema = z.enum(['queued', 'leased', 'done', 'dead']);
export type DeliveryStatus = z.infer<typeof DeliveryStatusSchema>;

/** One `(consumer_group, event)` audit row — who was delivered the event, and its outcome. */
export const DeliveryRecordSchema = z.object({
  consumerGroup: consumer,
  status: DeliveryStatusSchema,
  /** Read count (pgmq `read_ct`); 0 before first read. */
  attempts: z.number().int().nonnegative(),
  /** Epoch ms the delivery row was first created (fan-out). */
  deliveredAtMs: z.number().int().nonnegative(),
  /** Epoch ms the terminal ack/dead was recorded; null while still queued/leased. */
  ackedAtMs: z.number().int().nonnegative().nullable(),
  error: HubErrorSchema.nullable(),
});
export type DeliveryRecord = z.infer<typeof DeliveryRecordSchema>;

/** Per-event delivery rollup for the list view. */
export const DeliveryRollupSchema = z.object({
  total: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  dead: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
});
export type DeliveryRollup = z.infer<typeof DeliveryRollupSchema>;

/** A raw ledger row (no payload) — the shape storage returns for lists, traces and causation links. */
export const LedgerRowSchema = z.object({
  eventId: id,
  name: z.string().min(1).max(120),
  sourceService: z.string(),
  ownerId: id,
  correlationId: id,
  causationId: id.nullable(),
  orderingKey: z.string().nullable(),
  occurredAt: z.number().int().nonnegative(),
  /** Epoch ms the hub ingested (persisted) the event (`created_at`). */
  ingestedAtMs: z.number().int().nonnegative(),
});
export type LedgerRow = z.infer<typeof LedgerRowSchema>;

/** A ledger row plus its delivery rollup — one list entry. */
export const EventListRowSchema = LedgerRowSchema.extend({ deliveries: DeliveryRollupSchema });
export type EventListRow = z.infer<typeof EventListRowSchema>;

/** A list entry enriched with registry-derived display fields (what the API returns). */
export const EventSummarySchema = EventListRowSchema.extend({ topic: z.string(), title: z.string() });
export type EventSummary = z.infer<typeof EventSummarySchema>;

/** GET /api/hub/v1/events — filters (all optional; AND-combined). `cursor` is opaque. */
export const ListEventsQuerySchema = z.object({
  name: z.string().max(120).optional(),
  service: z.string().max(64).optional(),
  ownerId: z.string().max(64).optional(),
  correlationId: z.string().max(64).optional(),
  orderingKey: z.string().max(200).optional(),
  sinceMs: z.number().int().nonnegative().optional(),
  untilMs: z.number().int().nonnegative().optional(),
  limit: z.number().int().positive().max(200).optional(),
  cursor: z.string().max(200).optional(),
});
export type ListEventsQuery = z.infer<typeof ListEventsQuerySchema>;

export const ListEventsResultSchema = z.object({
  events: z.array(EventSummarySchema),
  nextCursor: z.string().nullable(),
});
export type ListEventsResult = z.infer<typeof ListEventsResultSchema>;
export const ListEventsResponseSchema = apiEnvelope(ListEventsResultSchema);
export type ListEventsResponse = z.infer<typeof ListEventsResponseSchema>;

/** A payload field descriptor (mirrors the registry's `PayloadFieldInfo`). */
export const PayloadFieldSchema = z.object({ field: z.string(), type: z.string(), note: z.string().optional() });
/** The payload contract-check for the detail view. */
export const PayloadCheckSchema = z.object({
  valid: z.boolean(),
  issues: z.array(z.string()),
  fields: z.array(PayloadFieldSchema),
});
export type PayloadCheck = z.infer<typeof PayloadCheckSchema>;

/** Ordering context for the detail view. */
export const OrderingInfoSchema = z.object({
  ordered: z.boolean(),
  orderingKey: z.string().nullable(),
  /** 0-based position of this event among events sharing its ordering key (null when unordered/no key). */
  index: z.number().int().nonnegative().nullable(),
  /** Count of events sharing the ordering key. */
  total: z.number().int().nonnegative().nullable(),
  /** An ordered key with a dead delivery — the flow is halted on it. */
  halted: z.boolean(),
});
export type OrderingInfo = z.infer<typeof OrderingInfoSchema>;

/** A delivery record annotated with whether the group was a declared consumer + its consume latency. */
export const DeliveryViewSchema = DeliveryRecordSchema.extend({
  declared: z.boolean(),
  /** occurredAt → ackedAt latency in ms; null until acked. */
  latencyMs: z.number().int().nonnegative().nullable(),
});
export type DeliveryView = z.infer<typeof DeliveryViewSchema>;

/** GET /api/hub/v1/events/:id — one event's full story. */
export const EventDetailSchema = z.object({
  event: LedgerRowSchema.extend({
    topic: z.string(),
    title: z.string(),
    payload: z.record(z.unknown()),
  }),
  payload: PayloadCheckSchema,
  ordering: OrderingInfoSchema,
  /** Service ids declared (manifest) as producers/consumers of this event name. */
  producers: z.array(z.string()),
  declaredConsumers: z.array(z.string()),
  /** Actual deliveries from the audit trail, annotated. */
  deliveries: z.array(DeliveryViewSchema),
  causation: z.object({
    parent: LedgerRowSchema.nullable(),
    children: z.array(LedgerRowSchema),
  }),
});
export type EventDetail = z.infer<typeof EventDetailSchema>;
export const EventDetailResponseSchema = apiEnvelope(EventDetailSchema);
export type EventDetailResponse = z.infer<typeof EventDetailResponseSchema>;

/**
 * ── The trace projection (ADR 0067, Amendment 2026-08-09) ──────────────────────────────
 *
 * The wire twin of the registry's `projectTrace()`. Every inferred type below matches the registry's
 * exported interface, so the registry stays the single public type and the schema is only the parse
 * boundary (ADR 0005).
 *
 * This replaced a flat `expectedNext: {event,title,via}[]`. The rename is the point, not cosmetics:
 * a lifecycle BRANCHES, so a completed run's `failed` and `cancelled` were never "expected" — they
 * were foreclosed. Leaving the old name in the contract would have invited the next consumer to
 * render them as pending work all over again, which is exactly how the defect reached the screen.
 */
export const LifecycleStepSchema = z.object({
  event: z.string(),
  title: z.string(),
  state: z.string(),
  kind: z.enum(['initial', 'transition', 'success', 'failure']),
  /** Derived, never declared: no transition leaves this state, so reaching it ends the lifecycle. */
  terminal: z.boolean(),
});

/** Where one lifecycle stands in this trace, and what may — or can no longer — follow. */
export const LifecycleProjectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  service: z.string(),
  aggregate: z.string(),
  /** The states this trace actually entered, oldest first. */
  path: z.array(LifecycleStepSchema),
  current: LifecycleStepSchema,
  settled: z.boolean(),
  outcome: z.enum(['success', 'failure']).nullable(),
  /** The mutually-exclusive branches still open. Empty once settled. */
  next: z.array(LifecycleStepSchema),
  /** Reachable when this trace began, unreachable now — endings that can no longer happen. */
  foreclosed: z.array(LifecycleStepSchema),
  /** Named by an event but unreachable from where the machine stood — out of order, or too late. */
  offMachine: z.array(LifecycleStepSchema),
});

export const FlowStepViewSchema = z.object({
  event: z.string(),
  title: z.string(),
  from: z.string(),
  to: z.string(),
  note: z.string().optional(),
  seen: z.boolean(),
});

/** How far one cross-service flow got in this trace. */
export const FlowProjectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  steps: z.array(FlowStepViewSchema),
  complete: z.boolean(),
  /** Index of the furthest hop seen; -1 when none. */
  furthest: z.number().int(),
  next: FlowStepViewSchema.nullable(),
});

/** One event that may still legitimately follow, and whether it is the sole successor or one of several. */
export const PossibleNextSchema = z.object({
  event: z.string(),
  title: z.string(),
  via: z.string(),
  branch: z.enum(['only', 'one-of']),
});

/** GET /api/hub/v1/trace/:correlationId — the whole saga/lifecycle instance for one correlation id. */
export const EventTraceSchema = z.object({
  correlationId: id,
  /** All events sharing the correlation id, ordered oldest → newest. */
  events: z.array(EventSummarySchema),
  //
  // The four projection fields carry DEFAULTS so that a response from a Hub that predates them still
  // parses. hub-api and agent-runner-api deploy as separate Pages projects off one green CI run, so
  // there is a window of minutes where one is new and the other is not; without this, the reader
  // would report "unreachable", which is its word for "the Hub is down" — a deploy lag wearing the
  // costume of an outage. The inferred type is unchanged (zod defaults are optional on input,
  // present on output), so every consumer still sees required fields.
  /** Each lifecycle this trace touched, folded through its state machine. */
  lifecycles: z.array(LifecycleProjectionSchema).default([]),
  /** Each catalogued flow this trace touched, with its hops marked seen/unseen. */
  flows: z.array(FlowProjectionSchema).default([]),
  /** Every lifecycle has ended — nothing further will arrive on this thread. */
  settled: z.boolean().default(false),
  /** What may still follow. Empty once `settled` — never the branches already foreclosed. */
  possibleNext: z.array(PossibleNextSchema).default([]),
});
export type EventTrace = z.infer<typeof EventTraceSchema>;
export const EventTraceResponseSchema = apiEnvelope(EventTraceSchema);
export type EventTraceResponse = z.infer<typeof EventTraceResponseSchema>;

// ── Saga occurrences (DEVOP-45) — real instances of a catalogued saga by correlation id ──

/**
 * At-a-glance health of one saga occurrence (DEVOP-45 D12a), derived by the app:
 *   • `dead`     — a delivery in this correlation was dead-lettered (from the hub.deliveries audit)
 *   • `complete` — the saga's terminal step event was seen (the choreography finished)
 *   • `stalled`  — incomplete and no activity for a while (past the app's stalled threshold)
 *   • `running`  — incomplete but recently active
 * (`halted` is deliberately not a value: an ordered-queue halt is the drain loop's state, not the
 * store's — a halted run surfaces here as `stalled`. Per-correlation halt attribution is a follow-up.)
 */
export const SagaHealthSchema = z.enum(['complete', 'running', 'stalled', 'dead']);
export type SagaHealth = z.infer<typeof SagaHealthSchema>;

/** One real occurrence of a saga: a correlation id whose events include the saga's event types. */
export const SagaInstanceSchema = z.object({
  correlationId: id,
  /** The correlation's owner (representative — the newest event's ownerId). */
  ownerId: z.string(),
  /** Which of the saga's event names were seen in this correlation. */
  matchedEvents: z.array(z.string()),
  /** At-a-glance health, derived by the app from completeness + age + the deliveries audit. */
  health: SagaHealthSchema,
  /** Earliest / latest `occurredAt` across the correlation's events (epoch ms). */
  firstAt: z.number().int().nonnegative(),
  lastAt: z.number().int().nonnegative(),
  /** Total events in the correlation (may exceed the saga's — a correlation can span more). */
  eventCount: z.number().int().nonnegative(),
});
export type SagaInstance = z.infer<typeof SagaInstanceSchema>;

/** GET /api/hub/v1/sagas/:id/instances — the saga (from the catalogue) + its recent occurrences. */
export const SagaInstancesResultSchema = z.object({
  sagaId: z.string(),
  name: z.string(),
  description: z.string(),
  /** The saga's distinct step event names (the match set). */
  stepEvents: z.array(z.string()),
  instances: z.array(SagaInstanceSchema),
});
export type SagaInstancesResult = z.infer<typeof SagaInstancesResultSchema>;
export const SagaInstancesResponseSchema = apiEnvelope(SagaInstancesResultSchema);
export type SagaInstancesResponse = z.infer<typeof SagaInstancesResponseSchema>;

/**
 * The capabilities the Hub's MACHINE routes require (ADR 0112). Publish is separated from consume
 * so a producer cannot drain the queue it writes to — before this, every bound caller reached
 * `os-hub-internal` with identical authority and differed only in which functions it happened to
 * call.
 *
 * The READ surface is gated too, and separately. `GET /v1/events/:id` returns an event's full
 * `payload` for any `ownerId`, and the Inspector routes sit on the same app the binding-only Worker
 * serves — where there is no Access gate either. So before this, any Worker holding the `HUB`
 * binding could read every other service's event bodies, including one whose only stated job was to
 * publish. `events.read` is deliberately NOT implied by `events.consume`: draining your own queue is
 * not a licence to read everyone else's traffic.
 *
 * `overview.view` covers the Monitor's aggregate health only — queue depth, lag, saga counts, no
 * payloads — which is why it is a separate, weaker capability rather than part of `events.read`.
 */
export const HUB_CAPABILITIES = {
  eventsPublish: 'hub:events.publish',
  eventsConsume: 'hub:events.consume',
  eventsRead: 'hub:events.read',
  traceRead: 'hub:trace.read',
  overviewView: 'hub:overview.view',
} as const;
